import { Injectable } from '@angular/core';
import { NavigationEnd } from '@angular/router';
import { Constants } from '@app/common/constants';
import { ComponentState } from '@app/models/component-state';
import { UserLanguageTerm } from '@app/models/user-language-term';
import { CacheService } from '@app/services/cache.service';
import { ComponentStatesOnRoutesService } from '@app/services/component-states-on-routes.service';
import { FeatureSettingsStoreService } from '@app/services/feature-settings-store.service';
import { NewsService } from '@app/services/news.service';
import { OrganizationCalendarService } from '@app/services/organization-calendar.service';
import { UserAppTileService } from '@app/services/user-app-tile.service';
import { UserItemsToReadService } from '@app/services/user-items-to-read.service';
import { addMinutes, isAfter } from 'date-fns';
import { forkJoin, Observable } from 'rxjs';
import { tap } from 'rxjs/operators';
import { UserLanguageTermService } from './user-language-term.service';

@Injectable({
  providedIn: 'root'
})
export class UpdatedContentService {

  constructor(private newsService: NewsService,
              private cacheService: CacheService,
              private userAppTileService: UserAppTileService,
              private organizationCalendarService: OrganizationCalendarService,
              private componentStatesOnRoutesService: ComponentStatesOnRoutesService,
              private userItemsToReadService: UserItemsToReadService,
              private featureSettingsStoreService: FeatureSettingsStoreService,
              private userLanguageTermService: UserLanguageTermService) {
    this.userLanguageTerms$ = this.userLanguageTermService.getUserLanguageTerms();
  }

  private readonly minutesBetweenUpdatedContentChecks = 30;
  private userLanguageTerms$: Observable<Array<UserLanguageTerm>>;

  onNavigationEnd(event: NavigationEnd): Observable<any[]> {
    // get our current routemap with all components on every route
    const routeMap = this.componentStatesOnRoutesService.state.RouteMap;
    const observableChecksArr = new Array<Observable<any>>();
    routeMap.forEach((componentStates, url) => {
      // We dont do checks for new content on the route that we're presently browsing
      if (url !== event.url) {
        componentStates.forEach(current => {
          // Get the last check
          let timeStamp = current.LastCheckedForNewContent;
          // If we dont have a current last check in the state we try the cache to see if there is a value there
          if (!timeStamp) {
            const timeStampValue = this.cacheService.get<string>(`${current.ComponentId}-${Constants.COMPONENT_VISITED_CACHEKEY_SUFFIX}`);
            timeStamp = timeStampValue ? new Date(timeStampValue) : new Date();
          }

          // Check if it has been an X amount fo time since the last check and that the current component state doesnt already have ShowBadge==true
          if (isAfter(new Date(), addMinutes(timeStamp, this.minutesBetweenUpdatedContentChecks)) && !current.ShowBadge) {
            // If we should do a new check for updated content we have a couple of different ways  to do it for each type of component
            switch (current.ComponentId) {
              case 'app-organization-calendar': {
                observableChecksArr.push(
                  this.organizationCalendarService.getNewOrUpdatedOrganizationCalendarEventsFromGraph(timeStamp)
                    .pipe(
                      tap((data) => {
                        if (data && data.length > 0) {
                          this.organizationCalendarService.reloadOrganizationCalendarEventsFromGraph();
                          this.componentStatesOnRoutesService.updateStates(
                            new ComponentState({
                              ComponentId: 'app-organization-calendar',
                              ShowBadge: true,
                              Data: data
                            }));
                        }
                      })));
                break;
              }
              case 'app-featured-news': {
                observableChecksArr.push(
                  this.newsService.getNewTopNews(timeStamp, this.userLanguageTerms$).pipe(
                    tap((data) => {
                      if (data && data.length > 0) {
                        this.newsService.reloadAllTopNews();
                        this.componentStatesOnRoutesService.updateStates(
                          new ComponentState({
                            ComponentId: 'app-featured-news',
                            ShowBadge: true,
                            Data: data
                          }));
                      }
                    })));
                break;
              }
              case 'app-my-app-tiles': {
                observableChecksArr.push(this.userAppTileService.getNewOrUpdatedUserAppTilesSinceLastVisit(timeStamp)
                  .pipe(
                    tap((data) => {
                      if (data && data.length > 0) {
                        this.userAppTileService.reload();
                        this.componentStatesOnRoutesService.updateStates(
                          new ComponentState({
                            ComponentId: 'app-my-app-tiles',
                            ShowBadge: true,
                            Data: data
                          }));
                      }
                    })));
                break;
              }
              case 'app-required-reading': {
                if (this.featureSettingsStoreService.state.enableRequiredReading) {
                  observableChecksArr.push(
                    this.userItemsToReadService.getNewOrUpdatedUserToReadSinceLastVisit(timeStamp)
                      .pipe(
                        tap((data) => {
                          if (data && data.length > 0) {
                            this.componentStatesOnRoutesService.updateStates(
                              new ComponentState({
                                ComponentId: 'app-required-reading',
                                ShowBadge: true,
                                Data: data
                              }));
                          }
                        })));
                }
                break;
              }
              default: {
                // do nothing
                break;
              }
            }
          }
        });
      }
    });
    return forkJoin(observableChecksArr);
  }
}
